import React, { useState, useEffect, useRef } from 'react';
import { Input, Card, Spin, Empty, Typography, message, Button, Descriptions } from 'antd';
import { SearchOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';

const { Search } = Input;

interface SearchResponse {
  found: boolean;
  tenant?: { intPkCompanyID: number; companyName: string };
  site?: { intPkCompanySiteID: number; siteName: string };
  router?: {
    intPkSiteCoreRouterID: number;
    routerName: string;
    routerToken: string;
    coreRouterIp: string;
    enable: number;
  };
}

const MasterStaffRouterSearchComponent: React.FC = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>('');
  const [result, setResult] = useState<SearchResponse | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const lastProcessedErrorCount = useRef(0);

  const [
    searchRouterData,
    searchResponse,
    searchError,
    searchLoading,
    ,
    searchErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/search-router-by-token');

  useEffect(() => {
    if (searchResponse !== null) {
      const responseData: any = searchResponse.responseData as SearchResponse;
      if (responseData) {
        setResult({
          found: responseData.found ?? false,
          tenant: responseData.tenant ?? undefined,
          site: responseData.site ?? undefined,
          router: responseData.router ?? undefined,
        });
        setHasSearched(true);
      }
    }
  }, [searchResponse]);

  useEffect(() => {
    if (
      searchError !== null &&
      searchErrorCount !== 0 &&
      searchErrorCount !== lastProcessedErrorCount.current
    ) {
      lastProcessedErrorCount.current = searchErrorCount;
      openNotification('error', 'Search Failed', searchError);
      setResult(null);
      setHasSearched(false);
    }
  }, [searchError, searchErrorCount, openNotification]);

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      message.warning('Please enter a router token');
      return;
    }
    setSearchValue(trimmedValue);
    setResult(null);
    setHasSearched(false);
    searchRouterData({ routerToken: trimmedValue });
  };

  const handleViewRouter = () => {
    if (result?.router?.intPkSiteCoreRouterID) {
      navigate(`/master-staff-router-details/${result.router.intPkSiteCoreRouterID}`);
    }
  };

  return (
    <>
      {contextHolder}
      <Card title="Search Router by Token" style={{ marginBottom: '24px' }}>
        <Search
          placeholder="Enter router token"
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={handleSearch}
          loading={searchLoading}
          style={{ maxWidth: '600px' }}
          size="large"
        />
      </Card>

      {searchLoading && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Typography style={{ marginTop: '16px', display: 'block' }}>
            Searching router...
          </Typography>
        </div>
      )}

      {!searchLoading && hasSearched && result && (
        <Card>
          {!result.found ? (
            <Empty
              description={
                <Typography.Text type="danger" style={{ fontSize: '16px' }}>
                  No router found for token: <strong>{searchValue}</strong>
                </Typography.Text>
              }
            />
          ) : (
            <>
              <Descriptions
                title="Router ownership"
                bordered
                column={{ xs: 1, sm: 1, md: 1 }}
                style={{ marginBottom: '24px' }}
              >
                <Descriptions.Item label="Tenant (Company)">
                  {result.tenant ? (
                    <span>
                      {result.tenant.companyName} (ID: {result.tenant.intPkCompanyID})
                    </span>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Site">
                  {result.site ? (
                    <span>
                      {result.site.siteName} (ID: {result.site.intPkCompanySiteID})
                    </span>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Router">
                  {result.router ? (
                    <span>
                      {result.router.routerName} — {result.router.coreRouterIp}
                      {result.router.enable === 1 ? (
                        <Typography.Text type="success"> (Enabled)</Typography.Text>
                      ) : (
                        <Typography.Text type="secondary"> (Disabled)</Typography.Text>
                      )}
                    </span>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
              </Descriptions>
              {result.router && (
                <Button
                  type="primary"
                  icon={<RightOutlined />}
                  onClick={handleViewRouter}
                  size="large"
                >
                  View Router Details
                </Button>
              )}
            </>
          )}
        </Card>
      )}

      {!searchLoading && !hasSearched && (
        <Card>
          <Empty description="Enter a router token above to search" />
        </Card>
      )}
    </>
  );
};

export default MasterStaffRouterSearchComponent;
